import {createSelector} from 'reselect';

import {FrameTypesEnum} from '../models/api/apiTypes';
import {AppStore} from '../store/appStore';
import {getFlatInboxes} from '../store/data/inboxes/inboxesSelectors';
import {updateSelectedSideFrameIdAction} from '../store/data/localPreferences/localPreferencesActions';
import {
  getLastSelectedSideFrameId,
  getSelectedSideFrameId,
} from '../store/data/localPreferences/localPreferencesSelectors';
import {getHiddenFrameIds} from '../store/data/preferences/preferencesSelectors';
import {getHasCellsEmptySetupActions} from '../store/featureFlags/featureFlagsSelectors';
import {getIsGuidedDemoActive} from '../store/stateless/guidedDemoSelectors';
import {getHasStartGuide, getIsStartGuideIncomplete} from '../store/stateless/startGuideSelectors';
import {getHasWelcomeCarousel} from '../store/stateless/welcomeCarouselSelectors';

/*
 * Setup.
 */

/** Sets up data for the start guide. */
export function setupStartGuide(store: AppStore) {
  // Potentially set the initial frame to the start guide.
  const state = store.getState();
  const initialFrameId = getInitialSelectedFrameId(state);
  store.dispatch(updateSelectedSideFrameIdAction(initialFrameId));
}

/*
 * Selectors.
 */

const getInitialSelectedFrameId = createSelector(
  [
    getSelectedSideFrameId,
    getLastSelectedSideFrameId,
    getHasStartGuide,
    getIsGuidedDemoActive,
    getHasWelcomeCarousel,
    getIsStartGuideIncomplete,
    getHasCellsEmptySetupActions,
    getFlatInboxes,
    getHiddenFrameIds,
  ],
  (
    selectedFrameId,
    lastSelectedFrameId,
    hasStartGuide,
    isGuidedDemoActive,
    hasWelcomeCarousel,
    isStartGuideIncomplete,
    hasCellsEmptySetupActions,
    inboxes,
    hiddenFrameIds,
  ) => {
    // If there's no selected frame, no last selected frame, we should show the
    // start guide, the start guide is incomplete, we don't have the guided
    // demo, we don't have `CellsEmptySetupActions`, and user has not hidden the
    // start guide, default to selecting the start guide.
    if (
      !selectedFrameId &&
      !lastSelectedFrameId &&
      hasStartGuide &&
      isStartGuideIncomplete &&
      !isGuidedDemoActive &&
      !hasWelcomeCarousel &&
      // If we have the CellsEmptySetupActions flag and no inboxes, don't
      // default-open the start guide.
      (hasCellsEmptySetupActions ? inboxes.length > 0 : true) &&
      !hiddenFrameIds.includes(FrameTypesEnum.START_GUIDE)
    ) {
      return FrameTypesEnum.START_GUIDE;
    }

    return selectedFrameId;
  },
);
